import React from 'react';
import { X } from 'lucide-react';
import { WalletQueryParams } from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface WalletFiltersProps {
  filters: WalletQueryParams;
  onChange: (filters: WalletQueryParams) => void;
}

export function WalletFilters({ filters, onChange }: WalletFiltersProps) {
  const handleReset = () => {
    onChange({
      maxResultCount: 50,
      skipCount: 0,
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Min Balance */}
      <div>
        <label className="text-sm font-medium mb-1.5 block">Min Balance</label>
        <Input
          type="number"
          placeholder="Min balance"
          value={filters.minBalance || ''}
          onChange={(e) =>
            onChange({
              ...filters,
              minBalance: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        />
      </div>

      {/* Max Balance */}
      <div>
        <label className="text-sm font-medium mb-1.5 block">Max Balance</label>
        <Input
          type="number"
          placeholder="Max balance"
          value={filters.maxBalance || ''}
          onChange={(e) =>
            onChange({
              ...filters,
              maxBalance: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        />
      </div>

      {/* Status */}
      <div>
        <label className="text-sm font-medium mb-1.5 block">Status</label>
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) =>
            onChange({
              ...filters,
              status:
                value === 'all' ? undefined : (value as 'active' | 'inactive'),
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sort By */}
      <div>
        <label className="text-sm font-medium mb-1.5 block">Sort By</label>
        <Select
          value={filters.sortBy || 'balance'}
          onValueChange={(value) =>
            onChange({
              ...filters,
              sortBy: value as WalletQueryParams['sortBy'],
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="balance">Balance</SelectItem>
            <SelectItem value="earned">Total Earned</SelectItem>
            <SelectItem value="userName">User Name</SelectItem>
            <SelectItem value="lastActivity">Last Activity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sort Order */}
      <div>
        <label className="text-sm font-medium mb-1.5 block">Sort Order</label>
        <Select
          value={filters.sortOrder || 'desc'}
          onValueChange={(value) =>
            onChange({
              ...filters,
              sortOrder: value as 'asc' | 'desc',
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Descending</SelectItem>
            <SelectItem value="asc">Ascending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reset Button */}
      <div className="flex items-end">
        <Button variant="outline" onClick={handleReset} className="w-full">
          <X className="w-4 h-4 mr-2" />
          Reset Filters
        </Button>
      </div>
    </div>
  );
}
